import { ModalOpenButton } from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import React from "react";
import { highlightWithMatches } from "../../utils.js";
import { CategoryInfoModal, hasCategoryInfo } from "./CategoryInfoModal.js";
import type { CategoryFragment } from "./useCategories.generated.js";

type CategoryWithMatch = CategoryFragment & {
  match: { index: number; length: number }[] | undefined;
};

type CategoryWithMatchAndChildren = CategoryWithMatch & {
  children?: CategoryWithMatchAndChildren[];
};

interface CategoryTreeItemsProps {
  categories: CategoryWithMatchAndChildren[];
  onCategoryClick?: (category: CategoryFragment) => void;
  excludeCategoryIds?: string[];
}

export const CategoryTreeItems: React.FC<CategoryTreeItemsProps> = ({
  categories,
  onCategoryClick,
  excludeCategoryIds,
}) => {
  const renderCategoryTree = (
    categories: CategoryWithMatchAndChildren[]
  ): React.ReactNode => {
    return categories
      .sort((a, b) => a.rank - b.rank)
      .map(category => (
        <TreeItem
          key={category.id}
          itemId={category.id}
          label={
            <Stack direction="row" spacing={2} alignItems="center">
              <Box display="flex" flexGrow={1}>
                <Typography>
                  {highlightWithMatches(category.name, category.match)}
                </Typography>
              </Box>
              {hasCategoryInfo(category) && (
                <Box display="flex" alignItems="center" height={0}>
                  <ModalOpenButton
                    Modal={CategoryInfoModal}
                    modalProps={{
                      category,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        onCategoryClick?.(category);
                      }}
                    >
                      <InfoOutlinedIcon color="secondary" />
                    </IconButton>
                  </ModalOpenButton>
                </Box>
              )}
            </Stack>
          }
          disabled={excludeCategoryIds?.includes(category.id) ?? false}
        >
          {category.children &&
            category.children.length > 0 &&
            renderCategoryTree(
              category.children.sort((a, b) => a.rank - b.rank)
            )}
        </TreeItem>
      ));
  };

  return <>{renderCategoryTree(categories)}</>;
};
